import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { createPortalApi } from '../../api/portalApi';
import { useRuntimeConfig } from '../../context/RuntimeConfigContext';
import { useUserRole } from '../../context/UserRoleContext';
import { useToast } from '../../components/Toast';
import Field from '../../components/settings/Field';
import type { OrgSettings } from '../../types';

const EMPTY: OrgSettings = { targetType: 'jira' };

export default function GitSettingsPage() {
  const { instance } = useMsal();
  const cfg           = useRuntimeConfig();
  const { isAdmin }   = useUserRole();
  const api            = createPortalApi(instance, cfg.msalClientId, cfg.apiBaseUrl);
  const { showToast }  = useToast();

  const [settings, setSettings] = useState<OrgSettings>(EMPTY);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');

  useEffect(() => {
    if (!isAdmin) return;
    api.getSettings()
      .then(s => setSettings(s ?? EMPTY))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  if (!isAdmin) return <Navigate to="/settings/profile" replace />;

  function set<K extends keyof OrgSettings>(key: K, value: OrgSettings[K]) {
    setSettings(s => ({ ...s, [key]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      await api.saveSettings(settings);
      showToast('Settings saved successfully.', 'success');
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="page-loading"><span className="spinner" />Loading settings…</div>;

  return (
    <div>
      <Link to="/settings" className="settings-back-link">← Organisation Settings</Link>

      {error && <div className="alert alert-error">{error}</div>}

      <form className="settings-form" onSubmit={handleSubmit}>
        <div className="settings-card">
          <h2 className="settings-section-title">GitHub Integration</h2>
          <p className="settings-section-desc">
            Repository the Web Automation Coder's "Push to repo" button pushes generated test scripts to.
            Users can override the token with their own under <strong>My Settings</strong>.
          </p>
          <div className="form-grid">
            <Field label="Repository Owner" placeholder="e.g. Fint-Solutions-Internal" value={settings.githubOwner         ?? ''} onChange={v => set('githubOwner', v)} />
            <Field label="Repository Name"  placeholder="e.g. automation-scripts"      value={settings.githubRepo          ?? ''} onChange={v => set('githubRepo', v)} />
            <Field label="Default Branch"  placeholder="master"                       value={settings.githubDefaultBranch ?? ''} onChange={v => set('githubDefaultBranch', v)} />
            <Field label="Personal Access Token" placeholder="GitHub PAT with repo contents write access" value={settings.githubToken ?? ''} onChange={v => set('githubToken', v)} secret />
          </div>
        </div>

        <div className="settings-footer">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save Settings'}
          </button>
        </div>
      </form>
    </div>
  );
}
